import React, { useState } from 'react';
import { API_CONFIG, APP_CONFIG } from '../config/envConfig';
import { trackSignupStarted } from '../utils/mixpanelClient';

const isUserAlreadyExistsError = (message) =>
  typeof message === 'string' && message.toLowerCase().includes('already exists');

const EarlyAccessForm = ({ mode = 'default', planId = null }) => {
  const isPricingFlow = mode === 'pricing-register';
  const [formData, setFormData] = useState({
    workEmail: '',
    businessUrl: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    const officialDomains = [
      "gmail.com",
      "yahoo.com", 
      "rediff.com",
      "yopmail.com",
      "outlook.com"
    ];

    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    if (officialDomains.includes(domain)) {
      setError('Please use your work email address');
      return false;
    }

    return true;
  };

  const validateUrl = (url) => {
    try {
      // Add https if not present
      // const urlToTest = url.startsWith('http') ? url : `https://${url}`;
      // new URL(urlToTest);
      if (!url.includes('.')) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!validateEmail(formData.workEmail)) {
      setError('Please enter your work email address');
      return;
    }

    // Validate URL
    // if (!validateUrl(formData.businessUrl)) {
    //   setError('Please enter a valid website URL');
    //   return;
    // }

    trackSignupStarted('email');
    setLoading(true);

    try {
      const endpoint = isPricingFlow
        ? API_CONFIG.pricingRegisterUrl
        : API_CONFIG.registerUrl;
      const payload = isPricingFlow
        ? {
            email: formData.workEmail,
            domain: formData.businessUrl,
            plan_id: planId ?? 4,
          }
        : {
            email: formData.workEmail,
            source: null,
            domain: formData.businessUrl
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Get the response data
      const responseData = await response.json();
      console.log('API Response:', responseData );

      if (!response.ok || responseData.status === 'failed') {
        setError(responseData.message || 'Unable to create account. Please try again.');
        return;
      }

      if (isPricingFlow) {
        const paymentLink = responseData?.data?.payment_link;
        if (!paymentLink) {
          setError('Payment link not available. Please try again.');
          return;
        }

        // API returns Razorpay hosted payment URL (rzp.io), redirect user to complete payment.
        window.location.href = paymentLink;
        return;
      }

      setSubmitted(true);
      const thankYouElement = document.getElementById('thankYouSection');
      thankYouElement?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  return (
    <div className="w-full max-w-md sm:max-w-xl rounded-2xl sm:rounded-3xl bg-white border border-slate-200 overflow-hidden mx-auto shadow-xl">
      {/* Header text */}
      {!submitted && (
        <div className="px-4 sm:px-6 md:px-8 pt-5 sm:pt-6 md:pt-8 pb-3 sm:pb-4 border-b border-slate-100">
          {/* <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.18em] text-indigo-500/80 mb-2">
            Get your free account
          </p> */}
          <h2 className="text-lg sm:text-xl font-medium text-slate-900 leading-snug">
            {isPricingFlow ? "You're almost there. Let's get you started." : 'Automate your sales process with WhiteOwl AI'}
          </h2>
          <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
            {isPricingFlow
              ? 'Enter your work email and company domain to continue securely.'
              : 'Use your work email and company domain to get started.'}
          </p>
        </div>
      )}

      {/* Form state */}
      {!submitted && (
        <form
          id="earlyAccessForm"
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 md:px-8 pt-3 sm:pt-4 md:pt-5 pb-4 sm:pb-5 md:pb-6 space-y-3 sm:space-y-4 md:space-y-5"
        >
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-800">
              Work email*
            </label>
            <input
              type="email"
              name="workEmail"
              value={formData.workEmail}
              onChange={handleChange}
              placeholder="you@company.com"
              className="w-full px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 focus:border-transparent transition-all"
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-800">
              Company domain*
            </label>
            <input
              name="businessUrl"
              value={formData.businessUrl}
              onChange={handleChange}
              placeholder="example.com"
              className="w-full px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 focus:border-transparent transition-all"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-xs sm:text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl px-3 py-2">
              {isPricingFlow && isUserAlreadyExistsError(error) ? (
                <>
                  User already exists. Please <a
                    href={APP_CONFIG.getLoginRedirectUrl()}
                    className="font-medium text-indigo-600 underline hover:text-indigo-800"
                  >
                    sign In
                  </a> to upgrade your account.{' '}
                </>
              ) : (
                error
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-1">
            <button
              type="submit"
              disabled={loading}
              className={`w-full sm:w-auto inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 active:scale-[0.98] transition-all whitespace-nowrap touch-manipulation ${
                loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? 'Submitting...' : isPricingFlow ? 'Proceed to Payment' : 'Sign up'}
              {!loading && (
                <span className="ml-2 text-base -mr-1">
                  →
                </span>
              )}
            </button>

            <p className="text-[10px] sm:text-xs text-slate-500 leading-relaxed text-center sm:text-left">
              {isPricingFlow
                ? "You'll be redirected to Razorpay for secure checkout."
                : 'By continuing, you consent to receive onboarding emails from WhiteOwl. You can unsubscribe anytime.'}
            </p>
          </div>
        </form>
      )}

      {/* Success state */}
      {submitted && (
        <div
          id="thankYouSection"
          className="px-4 sm:px-6 md:px-8 py-6 sm:py-7 md:py-8 text-center space-y-3 sm:space-y-4"
        >
          <div className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-emerald-50 border border-emerald-200 mb-1">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="text-base sm:text-lg md:text-xl font-medium text-slate-900">
              Check your inbox
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed px-2">
              We've sent a verification link to{' '}
              <span className="font-semibold text-slate-900 break-all">
                {formData.workEmail}
              </span>
              . Please click on the link to activate your account.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EarlyAccessForm; 